import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import { Desc, Subhead } from 'components/landing-pages-v2/shared/Typography';
import type { ILpComponentImageCard } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import Grid from './Grid';

const RenderImageCard = (entry: ILpComponentImageCard) => {
  const { fields } = entry;
  const desktopImageSourceUrl = fields?.desktopImage
    ? optimizeContentfulImageUrl(fields.desktopImage, { width: 1600, quality: 80 })
    : undefined;
  const mobileImageSourceUrl = fields?.mobileImage
    ? optimizeContentfulImageUrl(fields.mobileImage, { width: 1200, quality: 80 })
    : undefined;
  const mobileDescriptionDisplay = fields?.hideDescriptionOnMobile ? 'none' : 'block'; // By default, we hide description on mobile
  const isSmallPadding = fields?.verticalPadding === 'Small';

  return (
    <Col analyticsName={`cosmos-landing-page-image-card-${fields?.analyticsId}`} trackImpression>
      <Grid backgroundColor="$backgroundSecondary">
        <Col className={cn('col-span-12', isSmallPadding ? 'py-8 md:py-12' : 'py-12 md:py-34')}>
          <Col
            className={cn(
              'h-154 w-full overflow-hidden rounded-3xl bg-od-bg-primary',
              'md:h-113 md:flex-row',
              'lg:h-180',
            )}
          >
            <Row className="overflow-hidden md:flex-1">
              <Col className="justify-start p-8 md:p-10 lg:p-16">
                {fields?.subtitle && (
                  <Row className="grow pb-3 md:pb-0">
                    <Subhead>{fields.subtitle}</Subhead>
                  </Row>
                )}
                <Col>
                  <Col className="w-full max-w-62.5 min-h-23.25 pb-4 md:max-w-68 md:min-h-26.25 lg:max-w-107.5 lg:min-h-42">
                    <Text
                      as="h2"
                      className="block text-header-sm text-od-content-primary lg:text-header-lg"
                    >
                      {fields.title}
                      {fields.secondaryTitle && (
                        <Text
                          as="span"
                          className="text-header-sm text-od-content-tertiary lg:text-header-lg"
                        >
                          {' '}
                          {fields.secondaryTitle}
                        </Text>
                      )}
                    </Text>
                  </Col>
                  <Desc
                    display={mobileDescriptionDisplay}
                    $largerThanSM={{ display: 'block' }}
                    $largerThanMD={{ fontSize: 18 }}
                  >
                    {fields.description}
                  </Desc>
                  {fields?.ctaLink && fields?.ctaDisplayText && (
                    <Box className="pt-6 md:pt-8 lg:pt-12">
                      <Button
                        variant="primary"
                        aria-label={`Go to page to ${fields?.ctaDisplayText}`}
                        analyticsName={`cosmos-landing-page-image-card-cta-${fields?.analyticsId}`}
                        href={fields.ctaLink}
                        size="sm"
                        className="min-h-12 w-45"
                      >
                        {fields?.ctaDisplayText}
                      </Button>
                    </Box>
                  )}
                </Col>
              </Col>
            </Row>
            <Box className="min-h-0 flex-1 md:p-4 lg:p-5">
              <Box className="grid h-full w-full overflow-hidden md:rounded-2xl">
                <Image
                  src={mobileImageSourceUrl}
                  alt="illustration of a home"
                  loading="lazy"
                  decoding="async"
                  sources={
                    desktopImageSourceUrl
                      ? [{ srcSet: desktopImageSourceUrl, media: '(min-width: 768px)' }]
                      : undefined
                  }
                  className="block h-full w-full object-cover object-[80%]"
                />
              </Box>
            </Box>
          </Col>
        </Col>
      </Grid>
    </Col>
  );
};

const ImageCard: EntryComponent<ILpComponentImageCard> = {
  render: RenderImageCard,
};

export default ImageCard;
